import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useAuth } from '../hooks/use-auth';
import { useDivisions } from '../hooks/use-divisions';
import { useUserDivisions } from '../hooks/use-user-divisions';
import { Division } from '../types';

interface DivisionContextType {
  selectedDivisionId: string;
  setSelectedDivisionId: (divisionId: string) => void;
  divisions: Division[];
  currentDivision: Division | undefined;
  shouldShowDivisionSelector: boolean;
  hasMultipleDivisions: boolean;
  isLoadingDivisions: boolean;
  hasAccessToDivision: (divisionId: string) => boolean;
}

export const DivisionContext = createContext<DivisionContextType | undefined>(undefined);

interface DivisionProviderProps {
  children: ReactNode;
}

/**
 * Provider for managing division selection across the entire application
 * Fetches divisions from API (internal division management)
 */
export function DivisionProvider({ children }: DivisionProviderProps) {
  const { isAdmin: hasAdminRole, isAuthenticated } = useAuth();
  const [selectedDivisionId, setSelectedDivisionIdState] = useState<string>('');
  
  // Fetch user's accessible divisions from API (replaces JWT token division claims)
  const { 
    data: userDivisionsData, 
    isLoading: isLoadingUserDivisions 
  } = useUserDivisions();

  // For admins, fetch all divisions (useDivisions internally checks for admin role)
  const { 
    data: allDivisionsData, 
    isLoading: isLoadingAllDivisions 
  } = useDivisions({ active: true });

  // Use all divisions for admins, user's divisions for regular users
  const divisions: Division[] = hasAdminRole 
    ? (allDivisionsData?.divisions || [])
    : (userDivisionsData?.divisions || []);

  const isLoadingDivisions = hasAdminRole ? isLoadingAllDivisions : isLoadingUserDivisions;

  const hasMultipleDivisions = divisions.length > 1;

  // Set division ID based on localStorage and available divisions
  useEffect(() => {
    if (!isAuthenticated || divisions.length === 0) return;

    // Check localStorage for user preference
    const storedDivisionId = localStorage.getItem('selectedDivisionId');
    
    // Validate that stored division is accessible to user
    const validStoredDivision = divisions.find(div => div.id === storedDivisionId);
    
    if (validStoredDivision && !selectedDivisionId) {
      setSelectedDivisionIdState(storedDivisionId!);
    } else if (!selectedDivisionId && divisions.length > 0) {
      // Auto-select first available division
      setSelectedDivisionIdState(divisions[0].id);
    }
  }, [divisions, selectedDivisionId, isAuthenticated]);

  // Helper function to check division access
  const hasAccessToDivisionInternal = (divisionId: string): boolean => {
    return divisions.some(div => div.id === divisionId);
  };

  // Wrapper function for setSelectedDivisionId that persists to localStorage
  const setSelectedDivisionId = (divisionId: string) => {
    setSelectedDivisionIdState(divisionId);
    localStorage.setItem('selectedDivisionId', divisionId);
  };

  // Determine if division selector should be shown
  const shouldShowDivisionSelector = divisions.length > 1;

  // Get current division info
  const currentDivision = divisions.find(div => div.id === selectedDivisionId);

  // Debug logging
  useEffect(() => {
    console.log('[DivisionContext] Division info:', {
      hasMultipleDivisions,
      divisionsLength: divisions.length,
      shouldShowDivisionSelector,
      hasAdminRole,
      selectedDivisionId,
      currentDivision: currentDivision?.name,
      isLoadingDivisions,
    });
  }, [hasMultipleDivisions, divisions.length, shouldShowDivisionSelector, hasAdminRole, selectedDivisionId, currentDivision, isLoadingDivisions]);

  const contextValue: DivisionContextType = {
    selectedDivisionId,
    setSelectedDivisionId,
    divisions,
    currentDivision,
    shouldShowDivisionSelector,
    hasMultipleDivisions,
    isLoadingDivisions,
    hasAccessToDivision: hasAccessToDivisionInternal,
  };

  return (
    <DivisionContext.Provider value={contextValue}>
      {children}
    </DivisionContext.Provider>
  );
}

/**
 * Hook to use the division context
 * Throws error if used outside of DivisionProvider
 */
export function useDivisionContext() {
  const context = useContext(DivisionContext);
  if (context === undefined) {
    throw new Error('useDivisionContext must be used within a DivisionProvider');
  }
  return context;
}
